import '../broiler/poultryDesign.css';
import Segmented from '../shared/Segmented.js';
import Button from '../shared/Button.js';
// Auto-extracted by Phase 2 Round 2 (verbatim). See MIGRATION_PLAN §6.
import React from 'react';
import {getProgramColor} from '../lib/programColors.js';
import {buildLayerGroupPalette, layerGroupColor} from './layerGroupPalette.js';
import {getReadableText} from '../lib/styles.js';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {recordSeqNavOptions, dailySeqItems} from '../lib/recordSequence.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {buildLayerDailyExportColumns} from '../lib/dailyReportExports.js';
import {printRows} from '../lib/printExport.js';
import {fmtDate as fmt} from '../lib/dateUtils.js';
import {createSavedView, updateSavedView, deleteSavedView} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
// Loaded on the first Add Report click (LazyAdminAddReportModal), not at startup.
import AdminAddReportModal from '../shared/LazyAdminAddReportModal.tsx';
import OperationalListEmptyState from '../shared/OperationalListEmptyState.jsx';

import InlineNotice from '../shared/InlineNotice.jsx';

import {
  DailyCardList,
  feedLbsVal,
  tagVal,
  gritVal,
  countVal,
  check,
  mortText,
  commentText,
} from '../shared/DailyRecordCards.jsx';

const LayerDailyPage = React.lazy(() => import('./LayerDailyPage.tsx'));
import {useLayerReportAnchors} from './useLayerReportAnchors.js';
import LayerReportAnchorNotice from './LayerReportAnchorNotice.js';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import {resolveLayerGroupStatus} from '../lib/layerGroupStatus.js';
import type {LayerGroupStatusResolution} from '../lib/layerGroupStatus.js';
import {useLayerDailysProjectionRefresh, type LayerDailysProjectionRefresh} from './layerDailysProjectionRefresh.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {LayerDailysGroupDirectoryData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {usePaginatedResource} from '../app/data/usePaginatedResource.js';
import {sb} from '../lib/supabase.js';
import {matchesTeamMemberFilter, teamMemberOptions, teamMemberSelectValue} from '../lib/teamMemberFilter.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import Header from '../shared/Header.jsx';
import type {LayerGroup, LayerBatchRow, LayerHousingRow} from './layerDailyGroups.js';
import type {Notice} from '../shared/InlineNotice.js';
import type {SavedViewState} from '../lib/savedViewsApi.js';

const LAYER_DAILYS_SURFACE_KEY = 'layer.dailys';
// Frozen module constant, not an inline object literal: useRouteResource
// serializes params into its key, and a fresh {} every render would rebuild the
// key and re-run everything downstream of it forever.
const LAYER_DAILYS_DIRECTORY_PARAMS = Object.freeze({});
const VALID_LAYER_DAILY_SOURCE_FILTERS = new Set(['all', 'daily', 'addfeed']);

// NOT props from LayerEntry any more.
//
// These three were App's former-eager boot results — the ppp-layer-groups-v1
// blob and the two layer directories — threaded down through a single
// LayerEntry hand-off. LayerDailysRouter reads them itself now, through
// layer.dailys.groupDirectory, so `layerdailys` retires from all three frozen
// consumer lists in tests/fixtures/former-eager-dependencies.json. They are
// still passed from the ROUTER to the two surfaces below, which is a different
// thing entirely: the provenance derivation scores a hand-off in the DOMAIN
// ENTRY, because that is where a boot value enters the route.
//
// Typed with the element types layerDailyGroups.ts exports rather than
// `unknown[]`: the resource payload is this file's own, so the narrowing no
// longer has to be deferred to avoid making a claim about LayerContext.
type LayerDailysHubProps = {
  layerGroups: LayerGroup[];
  layerBatches: LayerBatchRow[];
  layerHousings: LayerHousingRow[];
  // The canonical blob read's three-state verdict, and the retry that clears a
  // failed one. Threaded from the router because the read lives above the
  // index/record branch.
  canonicalStatus: LayerGroupStatusResolution;
  retryCanonicalStatus: () => void;
  // The router owns one callback across the index and record route. In
  // particular, record deletion navigates back to the index before the read
  // resolves, so a record-local lifecycle fence would discard that refresh.
  refreshLayerDailysProjection: LayerDailysProjectionRefresh;
};

// One layer_dailys row. The select is `*` and the client carries no generated
// database types, so `data` arrives as `any`; this records the columns the view
// actually depends on — the reads below plus every column `rec` writes, since
// `{...r, ...rec}` merges one into the other.
//
// `date` is NOT optional: the filter row compares it relationally
// (`r.date >= fFrom`) with no guard, so the view already requires every report
// to carry its report date. The numeric columns are `number | string` because a
// report reaches this table from the app (numeric) and from the webform (text).
// `photos` stays `unknown` — the only use is an Array.isArray probe.
type LayerDailyRecord = {
  id?: string;
  date: string;
  team_member?: string | null;
  batch_label?: string | null;
  batch_id?: string | null;
  feed_type?: string | null;
  feed_lbs?: number | string | null;
  grit_lbs?: number | string | null;
  layer_count?: number | string | null;
  mortality_count?: number | string | null;
  mortality_reason?: string | null;
  group_moved?: boolean | null;
  waterer_checked?: boolean | null;
  comments?: string | null;
  source?: string | null;
  photos?: readonly unknown[] | null;
};

// One app_saved_views row, reduced to the columns this view reads. The names are
// the ones savedViewsApi's own select list already spells out; that module
// returns them as `unknown` because it never looks inside, so this is the first
// place that says what they are. `id` is the table's uuid primary key.
type SavedViewRow = {
  id: string;
  name?: string | null;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: unknown;
};

// What this surface actually stores in app_saved_views.view_state: its five
// filter fields, flat. See the note on the two write sites below — this is NOT
// the {filters, sortRules, viewMode} envelope savedViewsApi.SavedViewState
// declares.
type LayerDailysViewState = {fGroup: string; fTeam: string; fFrom: string; fTo: string; srcFilter: string};

// The same state as it comes BACK out of the jsonb column, which is a different
// claim from the one above: nothing guarantees a stored row still matches what
// this surface writes. The four filters are `unknown` so their
// `typeof === 'string'` restores stay real narrowing; `srcFilter` is `string`
// because its restore is narrowed by membership in a three-literal set instead.
type StoredLayerDailysViewState = {
  fGroup?: unknown;
  fTeam?: unknown;
  fFrom?: unknown;
  fTo?: unknown;
  srcFilter: string;
};

// The `e.message` half of the `e.message || String(e)` fallbacks below. A caught
// value is `unknown` under strict mode, and `instanceof Error` is the wrong
// probe: supabase-js rejects with plain PostgrestError objects that carry
// `message` without being Errors. Returning '' for a value with no usable
// message leaves the existing `||` fallbacks behaving exactly as before.
function errMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const message = (e as {message?: unknown}).message;
    if (message) return String(message);
  }
  return '';
}

function LayerDailysRouter() {
  const {authState} = useAuth();
  const location = useLocation();
  // Keep this fence above the index/record split. A successful record delete
  // immediately navigates to the index, but the router remains mounted under
  // /layer/dailys/* long enough for its paired action-time publication to
  // settle.
  const refreshLayerDailysProjection = useLayerDailysProjectionRefresh();
  // The group directory is read HERE, above the branch, for the same reason the
  // projection fence is: both surfaces need it and neither owns it alone. It is
  // also exactly where the LayerEntry prop hand-off used to land, which is what
  // makes layer.dailys.groupDirectory a replacement rather than an addition.
  const directoryResource = useRouteResource(RESOURCE.LAYER_DAILYS_GROUP_DIRECTORY, LAYER_DAILYS_DIRECTORY_PARAMS);
  const directory = directoryResource.data as LayerDailysGroupDirectoryData | null;
  // The element types layerDailyGroups.ts exports — it is the only module that
  // reads a field off any of these three — asserted once, here, at the one
  // point the resource payload and the consumers' contract meet.
  const layerGroups = (directory?.layerGroups || []) as LayerGroup[];
  const layerBatches = (directory?.layerBatches || []) as LayerBatchRow[];
  const layerHousings = (directory?.layerHousings || []) as LayerHousingRow[];
  // PENDING while the payload is null, FAILED when the hook errored or the
  // reader could not vouch for the blob, RESOLVED otherwise. A missing document
  // is resolved-and-empty. Everything downstream that offers a NEW group choice
  // is gated on this rather than on the row count.
  const canonicalStatus = resolveLayerGroupStatus({data: directory, error: directoryResource.error});
  const retryCanonicalStatus = directoryResource.refetch;
  const dailyId = location.pathname.startsWith('/layer/dailys/')
    ? location.pathname.slice('/layer/dailys/'.length) || null
    : null;
  if (dailyId) {
    return React.createElement(
      React.Suspense,
      {
        fallback: React.createElement(
          'div',
          {role: 'status', 'aria-live': 'polite', style: {padding: '2rem', textAlign: 'center'}},
          'Loading Layer Daily Record…',
        ),
      },
      React.createElement(LayerDailyPage, {
        sb,
        fmt,
        authState,
        refreshLayerDailysProjection,
        layerGroups,
        layerBatches,
        layerHousings,
        canonicalStatus,
      }),
    );
  }
  return React.createElement(LayerDailysHub, {
    layerGroups,
    layerBatches,
    layerHousings,
    canonicalStatus,
    retryCanonicalStatus,
    refreshLayerDailysProjection,
  });
}

// layerGroups / layerBatches / layerHousings are still handed in by the router
// (provenance unchanged). Since E12a only the record page reads the batch and
// housing rows; the hub reads layerGroups for one thing, the R06 dot palette.
const LayerDailysHub = ({
  layerGroups,
  canonicalStatus,
  retryCanonicalStatus,
  refreshLayerDailysProjection,
}: LayerDailysHubProps) => {
  // R06: the canonical group list is the one source of each row's dot color,
  // read once so a group keeps its color whatever the filters show.
  const groupPalette = buildLayerGroupPalette(layerGroups);
  const {authState} = useAuth();
  // The list keeps its own record rows optimistic, but Home's 45-day timeline
  // and Layers Home's complete dashboard history are separate shared
  // projections. The router-owned action-time callback publishes both full-row
  // projections together, rather than routing Layer writes through App's
  // global fan-out.
  const {useState, useEffect} = React;
  // Rows the admin Add Report modal just created, projected over the server
  // pages so they show before the next read. (The edit override and delete hide
  // projections went with the unreachable edit modal, E12a.)
  const [createdRecords, setCreatedRecords] = useState<LayerDailyRecord[]>([]);
  const [fGroup, setFGroup] = usePersistentViewState('layer.dailys.groupFilter', '');
  const [fTeam, setFTeam] = usePersistentViewState('layer.dailys.teamFilter', '');
  const [fFrom, setFFrom] = usePersistentViewState('layer.dailys.fromFilter', '');
  const [fTo, setFTo] = usePersistentViewState('layer.dailys.toFilter', '');
  const [srcFilter, setSrcFilter] = usePersistentViewState('layer.dailys.sourceFilter', 'all');
  const [notice, setNotice] = useState<Notice | null>(null);
  const [exportNotice, setExportNotice] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const [savedViewNotice, setSavedViewNotice] = useState<Notice | null>(null);
  // `authState` is `AuthIdentity | false | null`, and `?.` only short-circuits
  // on null/undefined — `false?.user` is a property read on a boolean, not a
  // short circuit. The leading `&&` is the same truthiness guard PigBatchesView
  // already uses, and it produces the identical value for all three states:
  // null and false both fall through the trailing `|| null`.
  const myProfileId = (authState && authState.user?.id) || null;
  const savedViewsQuery = useSavedViewsData(LAYER_DAILYS_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;
  const navigate = useNavigate();

  // The list's rows, its cursor, its cancellation and its stale-key protection
  // are the paginated resource's now. `reload` is in the page key on purpose:
  // Retry must start a NEW complete sequence rather than same-key refetch an
  // already-partial list — the same contract cattle.dailys.list documents.
  const dailysParams = React.useMemo(() => ({reload: reloadKey}), [reloadKey]);
  const dailysResource = usePaginatedResource<LayerDailyRecord, number>(RESOURCE.LAYER_DAILYS_LIST, dailysParams);
  const {
    error: listError,
    hasMore: listHasMore,
    loadMore,
    loadMoreError: listLoadMoreError,
    loadingMore: listLoadingMore,
  } = dailysResource;
  const loading = dailysResource.loading;
  const loadError = React.useMemo<Notice | null>(
    () =>
      listError
        ? {
            kind: 'error',
            message:
              'Could not load daily reports. Please refresh the page. (' +
              (errMessage(listError) || String(listError)) +
              ')',
          }
        : listLoadMoreError
          ? {
              kind: 'error',
              message:
                'Could not load more daily reports. (' +
                (errMessage(listLoadMoreError) || String(listLoadMoreError)) +
                ')',
            }
          : null,
    [listError, listLoadMoreError],
  );

  // The server pages, with this session's local mutations projected over them.
  // Order is what the old single array produced: newly created rows first, then
  // the pages in server order, each row replaced by its override and skipped if
  // hidden. De-duplicated by id because the append loop below used to do that.
  const records = React.useMemo(() => {
    const seen = new Set<LayerDailyRecord['id']>();
    const rows: LayerDailyRecord[] = [];
    for (const row of [...createdRecords, ...dailysResource.rows]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
    return rows;
  }, [createdRecords, dailysResource.rows]);

  // A Retry is a new server sequence, so its rows are authoritative again and
  // no transient local projection may leak into the new key.
  useEffect(() => {
    setCreatedRecords([]);
  }, [reloadKey]);

  // The eager "load every page" behaviour is unchanged; only the cursor moved.
  // `listLoadMoreError` is in the guard so a failed append stops the loop
  // instead of retrying it forever — the old `pgLoading` ref simply never
  // reset on failure, which had the same effect by accident.
  useEffect(() => {
    if (listHasMore && !listLoadingMore && !listLoadMoreError) {
      void loadMore();
    }
  }, [listHasMore, listLoadingMore, listLoadMoreError, loadMore]);

  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  const [showAddModal, setShowAddModal] = useState(false);
  const anchors = useLayerReportAnchors(sb);

  // `(g): g is string` annotates the SAME truthiness test `.filter(Boolean)` ran;
  // Boolean ignores filter's index/array arguments, so the surviving elements are
  // identical. The annotation is what lets each survivor be an <option> value.
  const allGroups = [...new Set(records.map((r) => r.batch_label).filter((g): g is string => Boolean(g)))].sort();
  const teamOpts = teamMemberOptions(records.map((r) => r.team_member));
  const filtered = records.filter(
    (r) =>
      (!fGroup || r.batch_label === fGroup) &&
      matchesTeamMemberFilter(r.team_member, fTeam) &&
      (!fFrom || r.date >= fFrom) &&
      (!fTo || r.date <= fTo) &&
      (srcFilter === 'all' ||
        (srcFilter === 'daily' && r.source !== 'add_feed_webform') ||
        (srcFilter === 'addfeed' && r.source === 'add_feed_webform')),
  );
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);

  function layerDailysViewState(): LayerDailysViewState {
    return {
      fGroup: fGroup || '',
      fTeam: fTeam || '',
      fFrom: fFrom || '',
      fTo: fTo || '',
      srcFilter: VALID_LAYER_DAILY_SOURCE_FILTERS.has(srcFilter) ? srcFilter : 'all',
    };
  }

  function applyLayerDailysSavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    // `view_state` is opaque jsonb and savedViewsApi hands the row back without
    // describing it. The four filters stay `unknown`, so the `typeof === 'string'`
    // tests below are the real narrowing instead of decoration over a claim.
    // `srcFilter` is `string` because its restore is narrowed by MEMBERSHIP: the
    // set holds three string literals, so `.has()` answers false for every other
    // value the column could hold and the restore falls back to 'all'. A `typeof`
    // test in front of that `.has()` would add no rule and reject nothing the
    // `.has()` did not already reject.
    const st = (view.view_state || {}) as StoredLayerDailysViewState;
    setFGroup(typeof st.fGroup === 'string' ? st.fGroup : '');
    setFTeam(typeof st.fTeam === 'string' ? st.fTeam : '');
    setFFrom(typeof st.fFrom === 'string' ? st.fFrom : '');
    setFTo(typeof st.fTo === 'string' ? st.fTo : '');
    setSrcFilter(VALID_LAYER_DAILY_SOURCE_FILTERS.has(st.srcFilter) ? st.srcFilter : 'all');
    setSavedViewNotice(null);
  }

  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    setSavedViewNotice(null);
    if (!id) return;
    applyLayerDailysSavedView(savedViews.find((v) => v.id === id));
  }

  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setSavedViewNotice(null);
    setShowSaveViewForm(true);
  }

  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setSavedViewNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      // REAL FINDING, surfaced rather than fixed: savedViewsApi declares
      // `viewState: SavedViewState`, i.e. the {filters, sortRules, viewMode}
      // envelope buildViewState() produces. This surface has never written that
      // envelope — it stores its five filter fields flat and
      // applyLayerDailysSavedView above reads them back flat, and
      // app_saved_views.view_state is opaque jsonb, so the two halves have
      // always agreed with each other and disagreed with the declared type.
      // Asserting through `unknown` records the mismatch at the one boundary it
      // happens; changing what is written here would orphan every stored
      // 'layer.dailys' view. Same assertion, same reason, in updateSelectedView.
      const created = (await createSavedView(sb, {
        surfaceKey: LAYER_DAILYS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: layerDailysViewState() as unknown as SavedViewState,
      })) as SavedViewRow | null;
      setShowSaveViewForm(false);
      setSavedViewNotice({kind: 'success', message: 'Saved view "' + name + '".'});
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Save view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      // Flat-vs-envelope assertion — see the note in submitSaveView above.
      await updateSavedView(sb, selectedView.id, {
        viewState: layerDailysViewState() as unknown as SavedViewState,
      });
      await loadSavedViews();
      setSavedViewNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current filters.',
      });
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Update view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setSavedViewNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Delete view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete saved view "' + selectedView.name + '"?', () => {
      void proceedDeleteSelectedView(selectedView);
    });
  }

  function handleExportCsv() {
    const columns = buildLayerDailyExportColumns();
    const ok = downloadCsv(csvFilename('layer-dailys'), rowsToCsv(columns, filtered));
    setExportNotice(ok ? '' : 'CSV export is only available in the browser.');
  }

  function handlePrintRows() {
    const columns = buildLayerDailyExportColumns();
    const ok = printRows({
      title: 'Layer Dailys',
      subtitle: filtered.length + ' filtered daily reports',
      columns,
      rows: filtered,
    });
    setExportNotice(ok ? '' : 'Print is only available in the browser.');
  }

  const fi = {
    padding: '6px 10px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    fontSize: 12,
    fontFamily: 'inherit',
    background: 'var(--surface)',
    width: 'auto',
  };
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  const savedViewGhostBtnS = {
    ...fi,
    color: 'var(--ink)',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
  const savedViewPrimaryBtnS = {
    ...savedViewGhostBtnS,
    border: `1px solid ${getProgramColor('layer')}`,
    color: getProgramColor('layer'),
  };
  const savedViewRadioLabelS = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: 'var(--ink)',
    cursor: 'pointer',
  };

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-layer-dailys-loaded={loading || loadError ? 'false' : 'true'}
    >
      <Header />
      <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <h1 className="poultry-page-title">Daily Reports</h1>
            <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>
              {records.length.toLocaleString()} total
            </div>
          </div>
          <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
            <Button
              type="button"
              data-layer-dailys-export-csv="1"
              onClick={handleExportCsv}
              disabled={loading || !!loadError}
            >
              Export CSV
            </Button>
            <Button
              type="button"
              data-layer-dailys-print="1"
              onClick={handlePrintRows}
              disabled={loading || !!loadError}
            >
              Print
            </Button>
            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              + Add Report
            </Button>
          </div>
        </div>
        {exportNotice && <div style={{marginBottom: 14, color: 'var(--danger-ink)', fontSize: 12}}>{exportNotice}</div>}
        {showAddModal && (
          <AdminAddReportModal
            sb={sb}
            formType="layer"
            onClose={() => setShowAddModal(false)}
            // The canonical read this hub already owns. The modal's own group
            // names come from the webform_config mirror, which lags a status
            // change by up to a maintenance run, so the verdict is what lets it
            // veto a group this page has already stopped listing - and refuse
            // to offer any group at all until that verdict exists.
            canonicalStatus={canonicalStatus}
            onRetryCanonicalStatus={retryCanonicalStatus}
            // AdminAddReportModal is generic over the row type it hands back —
            // it inserts through a client with no generated database types, so
            // the shape is the caller's to name. This annotation is what binds
            // that type parameter: the modal calls `onSaved(data)` with the rows
            // its layer_dailys insert selected back.
            onSaved={(recs: LayerDailyRecord[]) => {
              setCreatedRecords((p) => [...recs, ...p]);
              void refreshLayerDailysProjection().catch(() => {});
            }}
          />
        )}
        {!loadError && (
          <>
            <div
              data-layer-dailys-saved-views-row
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-layer-dailys-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-layer-dailys-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...fi, minWidth: 200}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        type="button"
                        data-layer-dailys-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-layer-dailys-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  {savedViewNotice && (
                    <span
                      style={{
                        fontSize: 12,
                        color: savedViewNotice.kind === 'success' ? 'var(--ok-ink)' : 'var(--danger-ink)',
                      }}
                    >
                      {savedViewNotice.message}
                    </span>
                  )}
                  <Button
                    type="button"
                    data-layer-dailys-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy || savedViewsLoading}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>
            {showSaveViewForm && (
              <div
                data-layer-dailys-saved-view-form
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  data-layer-dailys-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...fi, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveLayerDailysViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-layer-dailys-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveLayerDailysViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-layer-dailys-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button
                  type="button"
                  data-layer-dailys-saved-view-save
                  onClick={submitSaveView}
                  disabled={savedViewBusy}
                >
                  Save
                </Button>
                <Button type="button" onClick={() => setShowSaveViewForm(false)} disabled={savedViewBusy}>
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}
        <div style={{display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center'}}>
          <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} style={{...fi, width: 130}} />
          <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>to</span>
          <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={{...fi, width: 130}} />
          <select value={fGroup} onChange={(e) => setFGroup(e.target.value)} style={fi}>
            <option value="">All groups</option>
            {allGroups.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
          <select
            data-layer-dailys-team-filter="1"
            value={teamMemberSelectValue(fTeam, teamOpts)}
            onChange={(e) => setFTeam(e.target.value)}
            style={fi}
          >
            <option value="">All team members</option>
            {teamOpts.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {(fGroup || fTeam || fFrom || fTo || srcFilter !== 'all') && (
            <Button
              onClick={() => {
                setFGroup('');
                setFTeam('');
                setFFrom('');
                setFTo('');
                setSrcFilter('all');
              }}
            >
              Clear
            </Button>
          )}
          <Segmented
            aria-label="Report Source"
            value={srcFilter}
            onChange={setSrcFilter}
            options={[
              {value: 'all', label: 'All'},
              {value: 'daily', label: 'Daily Reports'},
              {value: 'addfeed', label: 'Add Feed'},
            ]}
            style={{marginLeft: 'auto'}}
          />
        </div>
        <InlineNotice notice={loadError} />
        {loadError && (
          <Button
            type="button"
            data-daily-list-retry="1"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{marginBottom: 12}}
          >
            Retry
          </Button>
        )}
        <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
        <LayerReportAnchorNotice anchors={anchors} />
        {loading && <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading...</div>}
        <OperationalListEmptyState
          loading={loading}
          loadError={loadError}
          totalCount={records.length}
          filteredCount={filtered.length}
          emptyLabel="No layer daily reports yet"
        />
        {!loading && !loadError && filtered.length > 0 && (
          <DailyCardList
            program="layer"
            // DailyCardList is still JavaScript, and its `rows = []` default is all
            // TypeScript has to infer that prop from, so it reads as `never[]` — a
            // description of the DEFAULT VALUE, not of the component, which reads
            // d.date / d.id off every row. The assertion records that mismatch at
            // the one boundary where it bites; it comes out when DailyRecordCards
            // itself converts.
            rows={filtered as unknown as never[]}
            fmt={fmt}
            maxInitialRows={100}
            // DailyRecordCards is JavaScript, so these three callbacks carry
            // their row annotation here. Each is handed one element of `rows`,
            // which is `filtered` above.
            onOpen={(row) => {
              const d = row as LayerDailyRecord;
              navigate('/layer/dailys/' + d.id, recordSeqNavOptions(dailySeqItems(filtered, 'batch_label')));
            }}
            rowAttrs={(row) => {
              const d = row as LayerDailyRecord;
              return {'data-daily-row': d.id};
            }}
            mapRow={(row) => {
              const d = row as LayerDailyRecord;
              return {
                name: d.batch_label || '—',
                dot: layerGroupColor(groupPalette, d.batch_label),
                team: d.team_member || '—',
                source: d.source,
                photos: d.photos,
                vals: {
                  feed: feedLbsVal(d.feed_lbs),
                  feedTag: d.feed_type ? tagVal(d.feed_type) : '',
                  grit: gritVal(d.grit_lbs),
                  count: countVal(d.layer_count),
                },
                checks: [check('Moved', d.group_moved !== false), check('Waterer', d.waterer_checked !== false)],
                mort: mortText(d.mortality_count, d.mortality_reason),
                comment: commentText(d.comments),
              };
            }}
          />
        )}
        {listHasMore && (
          <div style={{textAlign: 'center', padding: '0.5rem', fontSize: 11, color: 'var(--ink-faint)'}}>
            Loading more records...
          </div>
        )}
      </div>
    </div>
  );
};

export default LayerDailysRouter;
